import React, { useState, useEffect } from 'react';
import { Order } from '../types';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { Clock, CheckCircle2, AlertTriangle, Phone, FileText, Upload, Send, ClipboardList, X, Check } from 'lucide-react';
import { OperatorLogo, PaymentLogo, OPERATOR_CONFIGS } from '../components/common/BrandLogo';
import { compressImage } from '../lib/image';

export const OrdersPage: React.FC = () => {
  const { t } = useLanguage();
  const { user, isGuest } = useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploadOrderId, setUploadOrderId] = useState<string | null>(null);
  const [txRef, setTxRef] = useState('');
  const [slipUrl, setSlipUrl] = useState('');
  const [compressing, setCompressing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const data = await api.getOrders();
      setOrders(data || []);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isGuest) {
      loadOrders();
    } else {
      setLoading(false);
    }
  }, [isGuest]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('ကျေးဇူးပြု၍ ဓာတ်ပုံ သို့မဟုတ် Screenshot သာ ရွေးချယ်ပေးပါ');
      return;
    }
    setCompressing(true);
    try {
      const dataUrl = await compressImage(file);
      setSlipUrl(dataUrl);
    } catch {
      alert('ပုံရိပ် တင်သွင်းမှု မအောင်မြင်ပါ။ နောက်တစ်ကြိမ် ကြိုးစားပေးပါ။');
    } finally {
      setCompressing(false);
    }
  };

  const handleUploadSlip = async () => {
    if (!uploadOrderId) return;
    if (!slipUrl) {
      alert('ကျေးဇူးပြု၍ ငွေလွှဲပြေစာ Screenshot ထည့်သွင်းပေးပါ');
      return;
    }
    setSubmitting(true);
    try {
      await api.submitOrderSlip(
        uploadOrderId,
        txRef || 'DIRECT-SLIP',
        slipUrl
      );
      setUploadOrderId(null);
      setTxRef('');
      setSlipUrl('');
      loadOrders();
    } catch (err: any) {
      alert(err.message || 'Slip upload failed');
    } finally {
      setSubmitting(false);
    }
  };

  const getFulfillmentBadge = (status: Order['fulfillment_status']) => {
    switch (status) {
      case 'completed':
        return (
          <span className="inline-flex items-center text-[11px] font-bold text-emerald-400 bg-emerald-500/15 px-2.5 py-1 rounded-full border border-emerald-500/30">
            <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
            {t.status.completed}
          </span>
        );
      case 'processing':
        return (
          <span className="inline-flex items-center text-[11px] font-bold text-primary-light bg-primary/15 px-2.5 py-1 rounded-full border border-primary/30">
            <Clock className="w-3.5 h-3.5 mr-1 animate-spin" />
            {t.status.processing}
          </span>
        );
      case 'unable_to_deliver':
        return (
          <span className="inline-flex items-center text-[11px] font-bold text-red-400 bg-red-500/15 px-2.5 py-1 rounded-full border border-red-500/30">
            <AlertTriangle className="w-3.5 h-3.5 mr-1" />
            {t.status.unable_to_deliver}
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center text-[11px] font-bold text-amber-400 bg-amber-500/15 px-2.5 py-1 rounded-full border border-amber-500/30">
            <Clock className="w-3.5 h-3.5 mr-1" />
            {t.status.queued}
          </span>
        );
    }
  };

  if (isGuest) {
    return (
      <div className="space-y-6 pb-28 pt-6 text-center">
        <div className="bg-surface-card rounded-3xl border border-plum-border p-8 max-w-sm mx-auto shadow-xs">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mx-auto mb-4">
            <ClipboardList className="w-7 h-7" />
          </div>
          <h2 className="text-base font-bold text-plum-dark">{t.orders} (အော်ဒါများ)</h2>
          <p className="text-xs text-plum-muted mt-2 leading-relaxed">
            သင်၏ အော်ဒါမှတ်တမ်းများနှင့် ပို့ဆောင်မှုအခြေအနေ စစ်ဆေးရန် ကျေးဇူးပြု၍ Telegram Bot မှတစ်ဆင့် ဝင်ရောက်ပေးပါ။
          </p>
          <p className="text-[11px] text-plum-muted/80 mt-1">
            Please open via the Telegram Bot to view your order history and live delivery updates.
          </p>
          <a
            href="https://t.me/pluvp_bot"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 inline-flex items-center justify-center space-x-2 w-full py-2.5 px-4 bg-primary text-white rounded-xl text-xs font-semibold hover:bg-primary-hover transition shadow-sm"
          >
            <Send className="w-4 h-4" />
            <span>Telegram Bot တွင် ဖွင့်မည်</span>
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-24">
      <div className="flex items-center justify-between px-1">
        <h2 className="text-sm font-bold text-plum-dark">{t.orders} ({orders.length})</h2>
        <span className="text-[11px] text-plum-muted">အလိုအလျောက် Update လုပ်နေပါသည်</span>
      </div>

      <div className="space-y-3">
        {orders.map((ord) => (
          <div
            key={ord.id}
            className="bg-surface-card rounded-2xl border border-plum-border p-4 shadow-xs space-y-3"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3">
                <OperatorLogo
                  id={ord.operator_id}
                  size="md"
                  className="rounded-xl shadow-xs shrink-0 mt-0.5 border border-plum-border/40"
                />
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs font-bold text-plum-dark">
                      {OPERATOR_CONFIGS[ord.operator_id]?.name || ord.operator_id.toUpperCase()}
                    </span>
                    <span className="font-mono text-[11px] text-plum-muted">ID: {ord.id.slice(0, 8)}</span>
                  </div>
                  <div className="flex items-center space-x-1.5 text-xs text-plum-dark font-medium mt-1">
                    <Phone className="w-3.5 h-3.5 text-plum-muted" />
                    <span className="font-mono text-sm font-bold">{ord.recipient_phone}</span>
                  </div>
                </div>
              </div>

              <div className="text-right">
                <span className="text-base font-bold text-primary">
                  {ord.amount.toLocaleString()} Ks
                </span>
                <div className="flex items-center justify-end space-x-1 mt-1">
                  {ord.payment_method.includes('kpay') ? (
                    <PaymentLogo method="kpay" size="xs" className="rounded-md" />
                  ) : ord.payment_method.includes('wave') ? (
                    <PaymentLogo method="wavepay" size="xs" className="rounded-md" />
                  ) : null}
                  <span className="text-[10px] text-plum-muted uppercase font-mono font-medium">
                    {ord.payment_method.replace('direct_', '')}
                  </span>
                </div>
              </div>
            </div>

            {/* Status info */}
            <div className="pt-2 border-t border-plum-border/60 flex items-center justify-between">
              <div>
                {getFulfillmentBadge(ord.fulfillment_status)}
                {ord.delivery_ref && (
                  <p className="text-[10px] text-plum-muted mt-1 font-mono">
                    Ref: {ord.delivery_ref}
                  </p>
                )}
              </div>

              {/* Action if direct payment pending slip */}
              {ord.payment_method !== 'wallet' && ord.payment_status === 'pending' && (
                <button
                  onClick={() => setUploadOrderId(ord.id)}
                  className="bg-surface-accent hover:bg-primary/20 text-primary font-semibold text-xs py-1.5 px-3 rounded-xl border border-primary/30 flex items-center space-x-1 touch-target"
                >
                  <Upload className="w-3.5 h-3.5" />
                  <span>ပြေစာတင်မည်</span>
                </button>
              )}
            </div>
          </div>
        ))}

        {orders.length === 0 && !loading && (
          <div className="p-8 text-center bg-surface-card rounded-2xl border border-plum-border text-plum-muted text-xs">
            လတ်တလော Order များ မရှိသေးပါ။
          </div>
        )}
      </div>

      {/* Slip Upload Dialog */}
      {uploadOrderId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-plum-dark/60 backdrop-blur-xs p-4">
          <div className="bg-surface-card w-full max-w-sm rounded-3xl border border-plum-border p-5 shadow-2xl space-y-4">
            <h3 className="font-bold text-plum-dark text-base">ငွေလွှဲပြေစာ တင်သွင်းရန်</h3>
            <p className="text-xs text-plum-muted font-mono">Order: {uploadOrderId}</p>

            {/* Payment Slip Screenshot Upload */}
            <div>
              <label className="block text-xs font-semibold text-plum-dark mb-1.5">
                ငွေလွှဲပြေစာ Screenshot (Slip)
              </label>

              {slipUrl ? (
                <div className="relative rounded-2xl border border-primary/40 bg-surface-page p-2.5 flex items-center space-x-3">
                  <div className="w-14 h-14 rounded-xl overflow-hidden bg-black border border-plum-border shrink-0 flex items-center justify-center">
                    <img src={slipUrl} alt="Slip Preview" className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-1 text-xs font-bold text-status-green">
                      <Check className="w-3.5 h-3.5" />
                      <span>တင်ပြီးပါပြီ</span>
                    </div>
                    <p className="text-[10px] text-plum-muted truncate">စစ်ဆေးပြီး အတည်ပြုပါမည်</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setSlipUrl('');
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }}
                    className="p-1 rounded-lg hover:bg-red-500/20 text-plum-muted hover:text-status-red transition"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label
                  className={`relative flex flex-col items-center justify-center border-2 border-dashed rounded-2xl p-3 cursor-pointer transition text-center ${
                    compressing ? 'border-primary bg-primary/5' : 'border-plum-border hover:border-primary/50 bg-surface-page'
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    disabled={compressing}
                    className="sr-only"
                  />
                  <Upload className={`w-4 h-4 text-primary mb-1 ${compressing ? 'animate-bounce' : ''}`} />
                  <span className="text-xs font-bold text-plum-dark">
                    {compressing ? 'ပုံရိပ် ချုံ့နေပါသည်...' : 'ငွေလွှဲ Screenshot တင်ရန် နှိပ်ပါ'}
                  </span>
                  <span className="text-[10px] text-plum-muted mt-0.5">KPay / WavePay ပြေစာ</span>
                </label>
              )}
            </div>

            <div>
              <label className="block text-xs font-semibold text-plum-dark mb-1">
                Transaction ID (နောက်ဆုံး ၆ လုံး)
              </label>
              <input
                type="text"
                value={txRef}
                onChange={(e) => setTxRef(e.target.value)}
                placeholder="ဥပမာ: 887192"
                className="w-full bg-surface-page border border-plum-border rounded-xl px-3 py-2 text-sm font-mono"
              />
            </div>

            <div className="flex space-x-2 pt-2">
              <button
                type="button"
                onClick={() => setUploadOrderId(null)}
                className="flex-1 border border-plum-border py-2.5 rounded-xl text-xs font-semibold"
              >
                ပယ်ဖျက်မည်
              </button>
              <button
                type="button"
                disabled={submitting}
                onClick={handleUploadSlip}
                className="flex-1 bg-primary text-white py-2.5 rounded-xl text-xs font-semibold"
              >
                {submitting ? 'တင်နေပါသည်...' : 'အတည်ပြုမည်'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
